import { useState } from 'react';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import {
  useDefaultTemplateBackups,
  useDeleteDefaultTemplateBackup,
  useRefreshDefaultTemplate,
  useRestoreDefaultTemplate,
} from './useReleaseChecklist';

export interface BackupOption {
  partition: string;
  timestamp: string;
  items: number;
}

export interface UseReleaseTemplateBackupsResult {
  options: BackupOption[];
  selected: string;
  selectedItems: number;
  setSelected: (partition: string) => void;

  // Refresh-from-Confluence
  refresh: () => Promise<void>;
  refreshPending: boolean;

  // Restore-from-backup
  confirmRestoreOpen: boolean;
  openConfirmRestore: () => void;
  closeConfirmRestore: () => void;
  restore: () => Promise<void>;
  restorePending: boolean;

  // Delete-backup
  confirmDeleteOpen: boolean;
  openConfirmDelete: () => void;
  closeConfirmDelete: () => void;
  deleteSelected: () => Promise<void>;
  deletePending: boolean;
}

// Bundles the state, derived values, dialog wiring, and mutations the
// "Manage templates" panel needs. The consuming component just renders;
// success/error toasts and dialog dismissal are handled here so each
// callsite is a one-liner.
export const useReleaseTemplateBackups = (): UseReleaseTemplateBackupsResult => {
  const alertApi = useApi(alertApiRef);

  const [selected, setSelected] = useState('');
  const [confirmRestoreOpen, setConfirmRestoreOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { data: backupsData } = useDefaultTemplateBackups();
  const refreshTemplate = useRefreshDefaultTemplate();
  const restoreTemplate = useRestoreDefaultTemplate();
  const deleteTemplateBackup = useDeleteDefaultTemplateBackup();

  const options = backupsData ?? [];
  const selectedItems =
    options.find(b => b.partition === selected)?.items ?? 0;

  const refresh = async () => {
    try {
      const result = await refreshTemplate.mutateAsync({ source: 'confluence' });
      const backupSuffix = result.backupPartition
        ? ` (previous saved as ${result.backupPartition})`
        : '';
      const itemsText = `${result.items} item${result.items === 1 ? '' : 's'}`;
      if (result.fallbackReason) {
        alertApi.post({
          message: `Failed to fetch the template from ${result.source} (${result.fallbackReason}). Refreshed from the local stored template instead: ${itemsText}${backupSuffix}`,
          severity: 'warning',
        });
      } else {
        alertApi.post({
          message: `Refreshed default template from ${result.source}: ${itemsText}${backupSuffix}`,
          severity: 'success',
        });
      }
    } catch (e) {
      alertApi.post({
        message: `Failed to refresh template: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  const restore = async () => {
    if (!selected) return;
    const partition = selected;
    try {
      const result = await restoreTemplate.mutateAsync({ partition });
      setConfirmRestoreOpen(false);
      alertApi.post({
        message: `Restored default template from ${result.backupPartition}: ${
          result.items
        } item${result.items === 1 ? '' : 's'}`,
        severity: 'success',
      });
    } catch (e) {
      setConfirmRestoreOpen(false);
      alertApi.post({
        message: `Failed to restore backup: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    const partition = selected;
    try {
      const result = await deleteTemplateBackup.mutateAsync({ partition });
      setSelected('');
      setConfirmDeleteOpen(false);
      alertApi.post({
        message: `Deleted template backup ${result.partition} (${
          result.items
        } item${result.items === 1 ? '' : 's'})`,
        severity: 'success',
      });
    } catch (e) {
      setConfirmDeleteOpen(false);
      alertApi.post({
        message: `Failed to delete backup: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  return {
    options,
    selected,
    selectedItems,
    setSelected,

    refresh,
    refreshPending: refreshTemplate.isPending,

    confirmRestoreOpen,
    openConfirmRestore: () => setConfirmRestoreOpen(true),
    closeConfirmRestore: () => setConfirmRestoreOpen(false),
    restore,
    restorePending: restoreTemplate.isPending,

    confirmDeleteOpen,
    openConfirmDelete: () => setConfirmDeleteOpen(true),
    closeConfirmDelete: () => setConfirmDeleteOpen(false),
    deleteSelected,
    deletePending: deleteTemplateBackup.isPending,
  };
};
